import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { formatMoney } from "./quotePricingMath";
import { internalQuoteEconomicsFromComponents } from "./wixJobQuote";

function formatPct(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return `${n.toFixed(1)}%`;
}

function Metric({ label, value, hint, emphasize = false }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textTransform: "uppercase", letterSpacing: "0.08em" }}
            >
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: emphasize ? 800 : 700 }}>
                {value}
            </Typography>
            {hint ? (
                <Typography variant="caption" color="text.secondary">
                    {hint}
                </Typography>
            ) : null}
        </Box>
    );
}

export default function InternalQuoteEconomicsView({
    components,
    currency = "USD",
    customerTotal,
    actualHours = 0,
    extraMaterialCost = 0,
    sx,
}) {
    const economics = internalQuoteEconomicsFromComponents(components, {
        customerTotal,
        actualHours,
        extraMaterialCost,
    });
    if (!economics.trueCost && !economics.revenue && !economics.absorbedInternal && !economics.actualHours) return null;
    const profitPositive = economics.grossProfit >= 0;

    return (
        <Paper variant="outlined" sx={{ overflow: "hidden", ...sx }}>
            <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Internal margin
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Shop view only. Customer labor is billed as profit. Cost is PBR purchases, extra materials logged on the job, and any internal-only labor blocked from the customer quote.
                </Typography>
            </Box>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, minmax(0, 1fr))" },
                    gap: 1.5,
                    p: 1.5,
                }}
            >
                <Metric
                    label="Customer charge"
                    value={formatMoney(economics.revenue, currency)}
                    hint="What the customer quote bills"
                    emphasize
                />
                <Metric
                    label="Out of pocket"
                    value={formatMoney(economics.trueCost, currency)}
                    hint="PBR purchases + extra materials + internal-only labor"
                />
                <Metric
                    label="Gross profit"
                    value={formatMoney(economics.grossProfit, currency)}
                    hint={`${formatPct(economics.marginPct)} margin · ${formatPct(economics.markupOnCostPct)} on cost`}
                    emphasize
                />
                <Metric
                    label="Material markup"
                    value={formatMoney(economics.materialMarkupAmount, currency)}
                    hint={
                        economics.customerPurchaseCost
                            ? `${formatPct(economics.materialMarkupPercent)} on ${formatMoney(economics.customerPurchaseCost, currency)} cost`
                            : "No customer-facing PBR purchases"
                    }
                />
                <Metric
                    label="Materials charged"
                    value={formatMoney(economics.customerPurchaseCharge, currency)}
                    hint={`Cost ${formatMoney(economics.customerPurchaseCost, currency)}`}
                />
                <Metric
                    label="Internal-only absorbed"
                    value={formatMoney(economics.absorbedInternal, currency)}
                    hint={
                        economics.absorbedInternal
                            ? `${formatMoney(economics.internalLaborCost, currency)} labor · ${formatMoney(economics.internalPurchaseCost, currency)} purchases`
                            : "Nothing blocked from the customer quote"
                    }
                />
                <Metric
                    label="Labor billed (profit)"
                    value={formatMoney(economics.customerLaborCost, currency)}
                    hint={
                        economics.customerLaborHours
                            ? `${economics.customerLaborHours} hrs on the customer quote`
                            : "No billed labor"
                    }
                />
                <Metric
                    label="Internal-only labor"
                    value={formatMoney(economics.internalLaborCost, currency)}
                    hint={
                        economics.internalLaborHours
                            ? `${economics.internalLaborHours} hrs eaten (not billed)`
                            : "No blocked labor"
                    }
                />
                <Metric
                    label="Hours logged"
                    value={
                        economics.actualHours
                            ? `${Number(economics.actualHours).toLocaleString(undefined, { maximumFractionDigits: 2 })} hrs`
                            : "None yet"
                    }
                    hint={
                        economics.laborHours
                            ? `${economics.hoursVariance >= 0 ? "+" : ""}${Number(economics.hoursVariance).toLocaleString(undefined, { maximumFractionDigits: 2 })} vs ${economics.laborHours} quoted`
                            : "No quoted hours"
                    }
                />
                <Metric
                    label="Extra materials"
                    value={formatMoney(economics.extraMaterialCost, currency)}
                    hint="Logged on Job Actions, not on the customer quote"
                />
                <Metric
                    label="PBR purchase cost"
                    value={formatMoney(economics.purchaseCost, currency)}
                    hint={`Quoted buys · internal-only ${formatMoney(economics.internalPurchaseCost, currency)}`}
                />
            </Box>
            <Stack direction="row" spacing={1} sx={{ px: 1.5, pb: 1.25 }} flexWrap="wrap" useFlexGap>
                <Typography variant="caption" color={profitPositive ? "success.main" : "error.main"} sx={{ fontWeight: 700 }}>
                    {profitPositive ? "Positive" : "Negative"} margin at this customer total
                </Typography>
            </Stack>
        </Paper>
    );
}
